import styled from 'styled-components';
import { Link } from 'react-router-dom';

import { formatDateForDisplay } from 'helpers/dateTimes';
import { formatEuroAmount } from 'helpers/price';

import { GetOrganizationConsumptionQuery, GetOrganizationInvoiceSimulationQuery } from 'data/__generated__';

import { Amount, BlockTitle, Card, CardHeader, CardMeta, CardTitle, Muted } from './components/Card';
import { INVOICE_CATEGORY_LABELS, formatCount, formatPeriod } from './components/formatters';

type Consumption = GetOrganizationConsumptionQuery['getOrganizationConsumption'];
type InvoiceChannels = GetOrganizationInvoiceSimulationQuery['getOrganizationInvoiceSimulation']['totalsByCategory'];

// Ce que la facturation a fait d'une livraison Freefood (`OrderInvoicingStatus`, plus `Unknown` pour une
// commande sans registre).
const FREEFOOD_STATUS_LABELS: Record<string, string> = {
    Invoiced: 'facturé',
    Paid: 'payé',
    ToBeInvoiced: 'à facturer',
    Excluded: 'pas de facturation',
    Pending: 'livraison non enregistrée',
    Unknown: 'statut inconnu',
};

// Le Freefood a son propre bloc (toutes les livraisons de la période) : on l'écarte du tableau par canal,
// qui vient de la facture et ne connaît que les commandes restant à facturer.
const FREEFOOD_CATEGORY = 'freefood';

// Synthèse de la consommation d'une organisation sur la période, en deux moitiés qui viennent chacune de sa
// source unique :
//  - les VOLUMES (transactions payantes / gratuites, par colonne) : query `getOrganizationConsumption` ;
//  - les MONTANTS par canal (conso & RME, subvention, badge pro, démarque, Freefood) : `totalsByCategory` de
//    la simulation de facture. Aucun de ces montants n'est recalculé ici — c'est la facture qui les possède.
export const ConsumptionSection = ({
    consumption,
    invoiceChannels,
    loading,
    error,
}: {
    consumption?: Consumption;
    invoiceChannels?: InvoiceChannels;
    loading: boolean;
    error?: Error;
}) => {
    if (loading) {
        return (
            <Card>
                <Muted>Calcul de la consommation…</Muted>
            </Card>
        );
    }
    if (error) {
        return (
            <Card>
                <Muted>Impossible de calculer la consommation : {error.message}</Muted>
            </Card>
        );
    }
    if (!consumption) {
        return null;
    }

    const { columns, freefood } = consumption;
    // Une catégorie sans montant (groupe qui ne porte que des notes) n'apprend rien : le document en dessous
    // la détaille déjà.
    const channels = (invoiceChannels ?? []).filter(
        ({ category, amountHT }) => amountHT !== 0 && category !== FREEFOOD_CATEGORY,
    );
    const hasConsumption = consumption.transactionCount > 0 || channels.length > 0 || freefood.orderCount > 0;

    return (
        <Card>
            <CardHeader>
                <CardTitle>Consommation</CardTitle>
                <CardMeta>{formatPeriod(consumption.startDate, consumption.endDate)}</CardMeta>
            </CardHeader>
            <Hint>
                Volumes observés sur les sites de l'organisation, Freefood livré inclus. Les montants par canal facturé
                sont ceux de la facture ci-dessous (paliers, minimum de facturation et forfaits appliqués).
            </Hint>

            {!hasConsumption ? (
                <Muted>Aucune consommation sur la période.</Muted>
            ) : (
                <>
                    <Kpis>
                        <Kpi>
                            <KpiValue>{formatCount(consumption.transactionCount)}</KpiValue>
                            <KpiLabel>Transactions</KpiLabel>
                        </Kpi>
                        <Kpi>
                            <KpiValue>{formatCount(consumption.paidTransactionCount)}</KpiValue>
                            <KpiLabel>dont payantes</KpiLabel>
                        </Kpi>
                        <Kpi>
                            <KpiValue>{formatCount(consumption.freeTransactionCount)}</KpiValue>
                            <KpiLabel>dont gratuites</KpiLabel>
                        </Kpi>
                        <Kpi>
                            <KpiValue>{formatEuroAmount(consumption.consumedValueHT)}</KpiValue>
                            <KpiLabel>Valeur consommée HT</KpiLabel>
                        </Kpi>
                    </Kpis>

                    {channels.length > 0 && (
                        <Block>
                            <BlockTitle>Par canal facturé</BlockTitle>
                            <ChannelTable>
                                <HeaderCell>Canal</HeaderCell>
                                <HeaderCell $isNumeric>Montant HT</HeaderCell>
                                {channels.map(({ category, amountHT }) => (
                                    <Row key={category}>
                                        <Cell>{INVOICE_CATEGORY_LABELS[category] ?? category}</Cell>
                                        <Amount>{formatEuroAmount(amountHT)}</Amount>
                                    </Row>
                                ))}
                            </ChannelTable>
                        </Block>
                    )}

                    {freefood.orderCount > 0 && (
                        <Block>
                            <BlockTitle>
                                Freefood livré — {formatCount(freefood.orderCount)} commandes,{' '}
                                {formatEuroAmount(freefood.totalHT)} HT
                            </BlockTitle>
                            <FreefoodTable>
                                <HeaderCell>Commande</HeaderCell>
                                <HeaderCell>Livraison</HeaderCell>
                                <HeaderCell>Facturation</HeaderCell>
                                <HeaderCell $isNumeric>Total HT</HeaderCell>
                                {freefood.orders.map((order) => (
                                    <Row key={order.orderId}>
                                        <Cell>
                                            <OrderLink
                                                to={`/order/${order.orderId}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                N°{order.number}
                                            </OrderLink>
                                        </Cell>
                                        <Cell>{formatDateForDisplay(order.deliveryDate)}</Cell>
                                        <Cell>
                                            {FREEFOOD_STATUS_LABELS[order.invoicingStatus] ?? order.invoicingStatus}
                                            {order.odooInvoiceId ? ` (Odoo ${order.odooInvoiceId})` : ''}
                                        </Cell>
                                        <Amount>{formatEuroAmount(order.totalHT)}</Amount>
                                    </Row>
                                ))}
                            </FreefoodTable>
                        </Block>
                    )}

                    {columns.length > 0 && (
                        <Block>
                            <BlockTitle>Par colonne</BlockTitle>
                            <ColumnTable>
                                <HeaderCell>Colonne</HeaderCell>
                                <HeaderCell $isNumeric>Payantes</HeaderCell>
                                <HeaderCell $isNumeric>Gratuites</HeaderCell>
                                <HeaderCell $isNumeric>Total</HeaderCell>
                                <HeaderCell $isNumeric>Valeur HT</HeaderCell>
                                {columns.map((column) => (
                                    <Row key={column.microstoreColumnId}>
                                        <Cell>
                                            <ColumnLink
                                                to={`/microstore-column/${column.microstoreColumnId}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {column.name}
                                            </ColumnLink>
                                        </Cell>
                                        <Amount>{formatCount(column.paidTransactionCount)}</Amount>
                                        <Amount>{formatCount(column.freeTransactionCount)}</Amount>
                                        <Amount>{formatCount(column.transactionCount)}</Amount>
                                        <Amount>{formatEuroAmount(column.consumedValueHT)}</Amount>
                                    </Row>
                                ))}
                            </ColumnTable>
                        </Block>
                    )}
                </>
            )}
        </Card>
    );
};

const Hint = styled.p`
    margin: 0 0 12px 0;
    color: ${({ theme }) => theme.ctaPrimaryTextColor};
    font-size: 13px;
    font-style: italic;
`;

const Kpis = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 24px;
    margin-bottom: 16px;
`;

const Kpi = styled.div`
    display: flex;
    flex-direction: column;
`;

const KpiValue = styled.span`
    color: ${({ theme }) => theme.textColor};
    font-size: 20px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
`;

const KpiLabel = styled.span`
    color: ${({ theme }) => theme.ctaPrimaryTextColor};
    font-size: 13px;
`;

const Block = styled.div`
    margin-top: 16px;
`;

// Une seule grille par tableau : les colonnes restent alignées d'une ligne à l'autre.
const ChannelTable = styled.div`
    display: grid;
    grid-template-columns: 1fr auto;
    column-gap: 16px;
    color: ${({ theme }) => theme.textColor};
`;

const ColumnTable = styled(ChannelTable)`
    grid-template-columns: 1fr auto auto auto auto;
`;

const FreefoodTable = styled(ChannelTable)`
    grid-template-columns: auto auto 1fr auto;
`;

const Row = styled.div`
    display: contents;
`;

const HeaderCell = styled.div<{ $isNumeric?: boolean }>`
    padding: 4px 0;
    border-bottom: 1px solid ${({ theme }) => theme.lightBorderColor};
    color: ${({ theme }) => theme.ctaPrimaryTextColor};
    font-size: 13px;
    font-weight: 600;
    text-align: ${({ $isNumeric }) => ($isNumeric ? 'right' : 'left')};
`;

const Cell = styled.span`
    padding: 2px 0;
`;

const OrderLink = styled(Link)`
    color: ${({ theme }) => theme.ctaPrimaryTextColor};
    text-decoration: none;
    &:hover {
        text-decoration: underline;
    }
`;

const ColumnLink = styled(Link)`
    color: ${({ theme }) => theme.ctaPrimaryTextColor};
    text-decoration: none;
    &:hover {
        text-decoration: underline;
    }
`;
